import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { PosBadge, RenewalStatusBadge, StatusBadge } from '../components/Badge.jsx';
import OutreachStageDot, { stageTextColor } from '../components/OutreachStageDot.jsx';
import ChiBadge from '../components/ChiBadge.jsx';
import ColumnToggle, { LayoutDropdown, useColumnPreferences } from '../components/ColumnToggle.jsx';
import ColumnFilterMenu from '../components/ColumnFilterMenu.jsx';
import useColumnFilters from '../components/useColumnFilters.js';
import MergeToParentDialog from '../components/MergeToParentDialog.jsx';
import BackOfficeImportDialog from '../components/BackOfficeImportDialog.jsx';
import RecentImportBanner from '../components/RecentImportBanner.jsx';
import ColorLegend from '../components/ColorLegend.jsx';


// 8 preset highlight colors for the customer-list company-cell. Stored
// by name in customer_details.highlight_color. The picker on the
// CustomerDetail form keeps its own copy of the names.
const HIGHLIGHT_PALETTE = {
  orange: '#ED7D31',  // Excel Office Accent 2 -- exact match to Dave's spreadsheet
  red:    '#b91c1c',
  amber:  '#b45309',
  yellow: '#ca8a04',
  green:  '#047857',
  blue:   '#1e40af',
  purple: '#5b21b6',
  grey:   '#6b7280',
  // Outreach cadence colors (Dave 2026-06-09). Mirror the InactiveSites
  // page so filter chips behave the same on both views.
  cadenceYellow: '#a16207',
  cadenceOrange: '#c2410c',
  cadenceRed:    '#b91c1c'
};

const LEGEND_LABEL = {
  cadenceYellow: 'Initial sent',
  cadenceOrange: 'Follow-up sent',
  cadenceRed:    'Breakup sent'
};

// Effective highlight color for a customer row.
// Precedence (highest wins):
//   1. manual highlight_color set on the customer
//   2. price-increase email sent       -> orange  (#ED7D31, Excel Accent 2)
//   3. reactivation email sent         -> green   (#047857, theme --color-good)
// Returns null when none apply.
function effectiveColor(c) {
  // Outreach cadence wins over historical highlights (Dave 2026-06-09).
  if (c.outreach_stage === 'initial')  return 'cadenceYellow';
  if (c.outreach_stage === 'followup') return 'cadenceOrange';
  if (c.outreach_stage === 'breakup')  return 'cadenceRed';
  if (c.highlight_color)        return c.highlight_color;
  if (c.price_increase_sent)    return 'orange';
  if (c.reactivation_sent)      return 'green';
  return null;
}


function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 10);
}

// Short month + year (e.g. 'Sep 2026'). Used on the Customers list Next
// Renewal column so values group by month for sorting/filtering instead
// of being noisy per-day strings. The underlying ISO date still drives
// sort order, so within a month customers stay in date order; the
// dropdown filter buckets become ~12-18 months instead of 200+ dates.
const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
function formatMonthYear(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

// Relative-time formatter for xsiteio activity timestamps. Identical
// to the one on CustomerDetail (kept duplicated rather than extracted
// to a shared util so this file stays self-contained -- if we add a
// third caller we'll move it to client/src/lib).
function formatRelative(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  const ms = Date.now() - d.getTime();
  if (ms < 0) return d.toISOString().slice(0, 10);
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return 'just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 60) return `${day}d ago`;
  return d.toISOString().slice(0, 10);
}
function xsiteioTone(value) {
  if (!value) return 'var(--color-inactive)';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return 'var(--color-inactive)';
  const days = (Date.now() - d.getTime()) / 86400000;
  if (days <= 7)  return 'var(--color-good)';
  if (days <= 14) return 'var(--color-warn)';
  return 'var(--color-bad)';
}

function contactSubtext(c) {
  const parts = [c.first_name, c.last_name].filter(Boolean).join(' ');
  return parts || c.position || '';
}

function truncateCell(s, max = 24) {
  if (!s) return '';
  const v = String(s);
  return v.length > max ? v.slice(0, max - 1) + '…' : v;
}

function moneyFmt(n) {
  const v = typeof n === 'number' ? n : parseFloat(n);
  if (!Number.isFinite(v)) return '—';
  return `$${v.toLocaleString('en-CA', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

// Mirrors subscriptionsController.renewalStatus on the server — same
// 180-day threshold so the Account Summary column reads consistently
// with the renewal badge on the detail page.
function deriveRenewalStatus(iso, todayISO) {
  if (!iso) return '';
  const renewal = String(iso).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(renewal)) return '';
  if (renewal < todayISO) return 'Overdue';
  const days = Math.round(
    (new Date(renewal).getTime() - new Date(todayISO).getTime()) / 86400000
  );
  if (days <= 180) return 'Near';
  return 'Active';
}

// Column registry — `filterValue(row)` enables the per-column filter
// menu. The five groups match the gear-icon panel taxonomy:
//   • Contact Information — Company, Name, Position, Website
//   • Address             — Address 1/2, City, Province, Postal, Country
//   • Phone & Email       — Phone Main/Cell/Fax, Email 1/2/3
//   • Site Details        — Sites count, Site #, POS, Fuel Brand,
//                           Install/Renewal Date, Status (all
//                           sourced from the customer's primary site)
//   • Credentials         — Credentials count, legacy free-text creds
//
// defaultHidden columns sit out of the initial Main View — the user
// turns them on via the gear panel as needed (Email 2/3, Website,
// extra phone numbers, legacy free-text creds, etc.).
const COLUMNS = [
  // ── Site Number (Dave 2026-06-24) ─────────────────────────────
  // Promoted to the FIRST column so the Customers list reads with
  // the same column order as the Sites tab inside customer detail
  // (Site # / Name / Renewal / Status / freshness signals). For
  // multi-site chains this is the primary-site number; the company-
  // name cell still shows the "×N" multi-site badge so you can tell
  // a head office from a single-site at a glance.
  { key: 'site_number', label: 'Site #', group: 'site',
    tdClass: 'cell-mono',
    filterValue: (c) => c.site_number || '',
    cell: (c) => c.site_number || '' },

  // ── Contact Information ─────────────────────────────────────────
  {
    key: 'company', label: 'Name', group: 'contact',
    filterValue: (c) => c.company || '',
    // Visual cues on the company name:
    //   • Multi-site chains (site_count > 1) → bold + " ×N" suffix
    //   • Price Increase email logged       → orange (#ED7D31)
    //   • Reactivation (win-back) email sent → green  (#047857)
    // When both highlights apply, manual highlight_color > price-increase
    // > reactivation. Bold (multi-site) wins over semibold (highlight).
    cell: (c) => {
      const multiSite = Number(c.site_count) > 1;
      const priceIncrease = c.price_increase_sent;
      const reactivation  = c.reactivation_sent;
      // Highlight precedence: manual > price-increase > reactivation.
      const colorName = c.highlight_color
        || (priceIncrease ? 'orange'
        :  (reactivation  ? 'green'
        :  null));
      const hex = colorName ? HIGHLIGHT_PALETTE[colorName] : null;
      const style = {};
      if (multiSite) style.fontWeight = 'var(--font-bold)';
      else if (hex) style.fontWeight = 'var(--font-semibold)';
      if (hex) style.color = hex;
      // Outreach stage color wins over price-increase/reactivation tints
      // (Dave 2026-06-09): the cadence color is the most actionable signal
      // for Zoe/Kaden; the historical highlights are nice-to-know.
      const stageColor = stageTextColor(c.outreach_stage);
      if (stageColor) {
        style.color = stageColor;
        if (!multiSite) style.fontWeight = 'var(--font-semibold)';
      }
      const titles = [];
      if (multiSite) titles.push(`${c.site_count} sites`);
      if (c.highlight_color) titles.push(`Highlight: ${c.highlight_color}`);
      else if (priceIncrease) titles.push('Price Increase email has been sent');
      else if (reactivation)  titles.push('Reactivation email has been sent');
      // Outreach stage dot (Phase B): yellow/orange/red signifying
      // initial/followup/breakup. Sits to the LEFT of any color +
      // bold treatment above so the cadence color is unmistakable
      // and won't fight with the highlight palette.
      const wrapperStyle = Object.keys(style).length
        ? { ...style, display: 'flex', alignItems: 'center' }
        : { display: 'flex', alignItems: 'center' };
      return (
        <>
          <div
            className="cell-primary"
            style={wrapperStyle}
            title={titles.length ? titles.join(' · ') : undefined}
          >
            <OutreachStageDot stage={c.outreach_stage} />
            {c.company || '—'}
            {multiSite && (
              <span className="cell-mono" style={{ marginLeft: 'var(--space-2)', color: 'var(--color-text-dim)', fontWeight: 'var(--font-regular)' }}>
                ×{c.site_count}
              </span>
            )}
          </div>
          {contactSubtext(c) && <div className="cell-subtext">{contactSubtext(c)}</div>}
        </>
      );
    }
  },
  { key: 'first_name', label: 'First Name', group: 'contact', defaultHidden: true,
    cell: (c) => c.first_name || '' },
  { key: 'last_name', label: 'Last Name', group: 'contact', defaultHidden: true,
    cell: (c) => c.last_name || '' },
  { key: 'position', label: 'Position', group: 'contact', defaultHidden: true,
    filterValue: (c) => c.position || '',
    cell: (c) => c.position || '' },
  { key: 'website', label: 'Website', group: 'contact', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => truncateCell(c.website, 32) },

  // ── Address ─────────────────────────────────────────────────────
  { key: 'address1', label: 'Address 1', group: 'address', defaultHidden: true,
    cell: (c) => truncateCell(c.address1, 32) },
  { key: 'address2', label: 'Address 2', group: 'address', defaultHidden: true,
    cell: (c) => truncateCell(c.address2, 32) },
  { key: 'city', label: 'City', group: 'address', defaultHidden: true,
    filterValue: (c) => c.city || '',
    cell: (c) => c.city || '' },
  { key: 'province', label: 'Province', group: 'address', defaultHidden: true,
    filterValue: (c) => c.province_state || '',
    cell: (c) => c.province_state || '' },
  { key: 'postal_zip', label: 'Postal / Zip', group: 'address', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => c.postal_zip || '' },
  { key: 'country', label: 'Country', group: 'address', defaultHidden: true,
    filterValue: (c) => c.country || '',
    cell: (c) => c.country || '' },

  // ── Phone & Email ───────────────────────────────────────────────
  { key: 'phone_main', label: 'Phone Main', group: 'phoneEmail', tdClass: 'cell-mono', defaultHidden: true,
    cell: (c) => c.phone_main || '' },
  { key: 'phone_cell', label: 'Phone Cell', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => c.phone_cell || '' },
  { key: 'phone_fax', label: 'Phone Fax', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => c.phone_fax || '' },
  { key: 'email', label: 'Email', group: 'phoneEmail', defaultHidden: true,
    cell: (c) => <div className="cell-primary cell-mono">{c.email1 || ''}</div> },
  { key: 'email2', label: 'Email 2', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => c.email2 || '' },
  { key: 'email3', label: 'Email 3', group: 'phoneEmail', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => c.email3 || '' },

  // ── Site Details (sourced from each customer's primary site) ───
  { key: 'site_count', label: 'Sites', group: 'site', defaultHidden: true,
    cell: (c) => (
      <span className="badge badge-teal">{c.site_count ?? 0}</span>
    )
  },
  // Customer Health Index (CHI). Latest snapshot joined server-side by
  // customersController.list. Sortable numerically; NULL sorts to the
  // bottom. Grey badge when no snapshot yet (new customer, cron not
  // yet run). Dave 2026-06-30 -- Phase 2 of the CHI project.
  { key: 'chi_score', label: 'CHI', group: 'site',
    sortValue: (c) => c.chi_score ?? -1,
    cell: (c) => <ChiBadge score={c.chi_score} />
  },
  {
    // Sourced from nearest_renewal_date (earliest renewal across ALL of
    // the customer's sites) with a fall-back to the primary-site
    // renewal_date. For multi-site customers like Centex the primary
    // site often has no renewal on file, which used to render this cell
    // blank even when other sites had renewals.
    key: 'renewal_date', label: 'Next Renewal', group: 'site',
    // Display + filter on month/year only (e.g. 'Sep 2026'). The underlying
    // ISO date still drives sort order, so customers in the same month stay
    // in real date order; the dropdown filter buckets become ~12 months
    // instead of 200+ exact dates.
    filterValue: (c) => formatMonthYear(c.nearest_renewal_date || c.renewal_date) || '',
    cell: (c, ctx) => {
      const iso = formatDate(c.nearest_renewal_date || c.renewal_date);
      if (!iso) return <span className="cell-muted">—</span>;
      const monthYear = formatMonthYear(c.nearest_renewal_date || c.renewal_date);
      const overdue = iso < ctx.todayISO;
      // Three states, not two:
      //   overdue  → red    (needs action -- the alert Dave wants here)
      //   future   → green  (healthy, on schedule)
      //   (no date is handled above by the early return)
      const dotClass = overdue ? 'status-dot-red' : 'status-dot-green';
      const textStyle = overdue
        ? { color: 'var(--color-bad, #b91c1c)', fontWeight: 'var(--font-semibold)' }
        : undefined;
      return (
        <>
          <span className={`status-dot ${dotClass}`} />
          <span className="cell-mono" style={textStyle}>{monthYear}</span>
        </>
      );
    }
  },
  // xsiteio "Last Pricebook Update" — populated by the nightly sync. We
  // only show a value for SINGLE-site customers because the underlying
  // column comes from the primary site only, and for multi-site
  // customers showing one site's value would be misleading (Centex has
  // 82 sites with very different activity patterns). Multi-site rows
  // get a "—" with a tooltip explaining why.
  { key: 'xsiteio_last_updated', label: 'Last Pricebook Update', group: 'site',
    filterValue: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) return '';
      return c.xsiteio_last_updated ? formatRelative(c.xsiteio_last_updated) : '—';
    },
    cell: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) {
        return <span className="cell-muted" title="Multi-site customer — open the record to see per-site activity">—</span>;
      }
      return (
        <span className="cell-mono" title={c.xsiteio_last_updated || 'no data'}>
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: '50%', marginRight: 6,
            background: xsiteioTone(c.xsiteio_last_updated)
          }} />
          {formatRelative(c.xsiteio_last_updated)}
        </span>
      );
    }
  },
  // site_number was promoted to the top of the array -- see the
  // Dave 2026-06-24 block above.

  // Status column -- ACTIVE / INACTIVE badge sourced from
  // primary_site.status (returned as site_status from the customers
  // query). Single-site customers show their site's status; multi-
  // site customers show whichever the primary (preferred-Active)
  // site reports, which matches the convention for renewal_date /
  // xsiteio_last_updated above.
  { key: 'status', label: 'Status', group: 'site',
    filterValue: (c) => c.site_status || '',
    cell: (c) => c.site_status
      ? <StatusBadge value={c.site_status} />
      : <span className="cell-muted">—</span>
  },

  // Last Login (xsiteio) -- same colored-dot freshness convention
  // as Last Pricebook Update below. Single-site only; multi-site
  // rows show "—" because picking one site's value would mislead.
  { key: 'xsiteio_last_login', label: 'Last Login', group: 'site',
    filterValue: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) return '';
      return c.xsiteio_last_login ? formatRelative(c.xsiteio_last_login) : '—';
    },
    cell: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) {
        return <span className="cell-muted" title="Multi-site customer — open the record to see per-site activity">—</span>;
      }
      return (
        <span className="cell-mono" title={c.xsiteio_last_login || 'no data'}>
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: '50%', marginRight: 6,
            background: xsiteioTone(c.xsiteio_last_login)
          }} />
          {formatRelative(c.xsiteio_last_login)}
        </span>
      );
    }
  },

  // Last Fuel & Financial Done -- dd-mm-yyyy date, same single-
  // site-only constraint as the two columns above. The cell on the
  // customer detail page already handles a "not subscribed to FM"
  // state via the subscription record; that signal isn't available
  // on the aggregated customers list query yet, so the list cell
  // just shows the recon date or "—" without the badge for now.
  { key: 'xsiteio_last_fuel_recon_at', label: 'Last Fuel & Financial Done', group: 'site',
    filterValue: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) return '';
      return c.xsiteio_last_fuel_recon_at ? formatRelative(c.xsiteio_last_fuel_recon_at) : '—';
    },
    cell: (c) => {
      const sites = Number(c.site_count) || 0;
      if (sites !== 1) {
        return <span className="cell-muted" title="Multi-site customer — open the record to see per-site activity">—</span>;
      }
      // Not subscribed to Fuel Manager (primary_fm_renewal_date is
      // null) → neutral-grey badge, matches the customer detail
      // panel. Dave 2026-06-24.
      if (!c.primary_fm_renewal_date) {
        return (
          <span style={{
            display: 'inline-flex', alignItems: 'center',
            padding: '2px 7px',
            borderRadius: 'var(--radius-sm)',
            fontFamily: 'var(--font-mono)',
            fontSize: '10.5px',
            fontWeight: 'var(--font-semibold)',
            letterSpacing: 'var(--tracking-wide)',
            textTransform: 'uppercase',
            background: '#e5e7eb',
            color: '#4b5563',
            whiteSpace: 'nowrap'
          }}>Not subscribed</span>
        );
      }
      return (
        <span className="cell-mono" title={c.xsiteio_last_fuel_recon_at || 'no data'}>
          <span style={{
            display: 'inline-block', width: 8, height: 8,
            borderRadius: '50%', marginRight: 6,
            background: xsiteioTone(c.xsiteio_last_fuel_recon_at)
          }} />
          {c.xsiteio_last_fuel_recon_at
            ? new Date(c.xsiteio_last_fuel_recon_at).toISOString().slice(0,10).split('-').reverse().join('-')
            : '—'}
        </span>
      );
    }
  },
  { key: 'pos_type', label: 'POS Type', group: 'site', defaultHidden: true,
    filterValue: (c) => c.pos_type || '',
    cell: (c) => <PosBadge value={c.pos_type} /> },
  { key: 'fuel_brand', label: 'Fuel Brand', group: 'site', defaultHidden: true,
    filterValue: (c) => c.fuel_brand || '',
    cell: (c) => c.fuel_brand || '' },
  { key: 'install_date', label: 'Install Date', group: 'site',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (c) => formatDate(c.install_date) },
  // First Contact Date — when XSite first made contact with this
  // account (customer_details.first_contact_date). For multi-site head
  // offices like Centex Petroleum the parent record isn't itself a
  // site, so this is the only customer-level date that matters. Hidden
  // by default — opt in via the gear panel when filtering by tenure.
  { key: 'first_contact_date', label: 'First Contact', group: 'site',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (c) => formatDate(c.first_contact_date) },

  // ── Account Summary (off by default — opt in via the gear panel) ─
  // Three roll-up columns aggregated across every site + subscription
  // for the customer. Useful for a wide "portfolio" layout that
  // surfaces revenue and renewal risk at a glance.
  { key: 'active_site_count', label: 'Active Sites', group: 'accountSummary', defaultHidden: true,
    cell: (c) => (
      <span className="badge badge-teal">{c.active_site_count ?? 0}</span>
    )
  },
  { key: 'total_annual_value', label: 'Total Annual Value', group: 'accountSummary', defaultHidden: true,
    tdClass: 'cell-mono',
    cell: (c) => moneyFmt(c.total_annual_value)
  },
  { key: 'renewal_status', label: 'Renewal Status', group: 'accountSummary', defaultHidden: true,
    // useColumnFilters invokes filterValue(row) with no second arg, so
    // the filter menu can't lean on the table-level todayISO — recompute
    // it here so the dropdown groups rows by their current bucket.
    filterValue: (c) => deriveRenewalStatus(c.nearest_renewal_date, new Date().toISOString().slice(0, 10)) || '',
    cell: (c, ctx) => {
      const status = deriveRenewalStatus(c.nearest_renewal_date, ctx.todayISO);
      return <RenewalStatusBadge value={status} />;
    }
  },

  // ── Credentials ─────────────────────────────────────────────────
  { key: 'credentials_count', label: 'Credentials', group: 'credentials', defaultHidden: true,
    cell: (c) => (
      <span className="badge badge-grey">{c.credentials_count ?? 0}</span>
    )
  },
  // Legacy free-text creds — opt-in via the gear panel for the rare
  // case where a customer still has them (newer customers don't).
  { key: 'ftp_credentials', label: 'FTP Credentials (legacy)', group: 'credentials',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (c) => truncateCell(c.ftp_credentials) },
  // Dave 2026-06-24: dropped username_password (legacy free-text
  // creds) column, xsiteio_client_id column, and xsite_io_password
  // column from the customer list. The data still lives on each
  // customer detail page; just not surfaced on this list since none
  // of them are useful at-a-glance. ftp_credentials (legacy) is kept
  // because the rare customer who still has those needs them visible.

  // ── xsiteio columns (Dave 2026-06-11) ──────────────────────────
  // Aggregated per customer from the first site with a linked
  // xsiteio_client_id. Multi-site customers get a "(N of M linked)"
  // hint next to USERNAME so you know there's more than one set.
  { key: 'xsite_io_username', label: 'Username (xsiteio)', group: 'xsiteio',
    tdClass: 'cell-mono', defaultHidden: true,
    cell: (c) => {
      if (!c.xsite_io_username) return <span className="cell-muted">—</span>;
      const total = Number(c.site_count || 0);
      const linked = Number(c.xsiteio_linked_count || 0);
      const showHint = total > 1 && linked > 0;
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span>{c.xsite_io_username}</span>
          {showHint && (
            <span className="cell-muted" style={{ fontSize: 'var(--text-caption)' }}>
              ({linked} of {total} linked)
            </span>
          )}
        </span>
      );
    }
  },
  { key: 'xsiteio_active', label: 'xsiteio Status', group: 'xsiteio',
    cell: (c) => <XsiteioActiveToggle customer={c} />
  }
];
const DEFAULT_KEYS = COLUMNS.filter((c) => !c.defaultHidden).map((c) => c.key);

// xsiteio active/inactive slider toggle. Aggregates the customer's
// linked sites: all active -> green/ON; all inactive -> red/OFF;
// mixed -> half-half stripe; not linked -> diagonal-stripe UNKNOWN.
// Clicking opens a confirm dialog naming the customer + site count,
// then POSTs /api/customers/:id/xsiteio/set-active which flips
// active_inactive on every linked site. Refreshes the row after.
function XsiteioActiveToggle({ customer }) {
  const c = customer;
  const active   = Number(c.xsiteio_active_count   || 0);
  const inactive = Number(c.xsiteio_inactive_count || 0);
  const linked   = Number(c.xsiteio_linked_count   || 0);
  const [busy, setBusy] = useState(false);

  let state, label;
  if (linked === 0) {
    // Not linked to xsiteio at all.
    state = 'unknown'; label = 'UNKNOWN';
  } else if (active === 0 && inactive === 0) {
    // Linked but never Pulled — xsiteio_active is NULL on every linked
    // site. Show as UNKNOWN with linked count so Dave knows there's
    // data to fetch (Dave 2026-06-11).
    state = 'unknown';
    label = linked > 1 ? `UNKNOWN (${linked} linked)` : 'UNKNOWN (linked)';
  } else if (active > 0 && inactive === 0) {
    state = 'active'; label = linked > 1 ? `ACTIVE (${active})` : 'ACTIVE';
  } else if (inactive > 0 && active === 0) {
    state = 'inactive'; label = linked > 1 ? `INACTIVE (${inactive})` : 'INACTIVE';
  } else {
    state = 'mixed'; label = `${active} active, ${inactive} inactive`;
  }

  async function handleClick(e) {
    e.stopPropagation();
    if (busy) return;
    if (linked === 0) {
      alert('No xsiteio-linked sites under this customer to toggle.');
      return;
    }
    const target = state === 'active' ? false : true;  // flip the dominant state
    const verb = target ? 'ACTIVATE' : 'DEACTIVATE';
    const company = c.company || c.last_name || 'this customer';
    const ok = window.confirm(
      `${verb} ${linked} site${linked === 1 ? '' : 's'} in xsiteio under ${company}?\n\n` +
      (target
        ? 'All linked sites will be re-enabled in xsiteio.'
        : 'All linked sites will stop reporting and stop billing in xsiteio until re-activated.')
    );
    if (!ok) return;
    setBusy(true);
    try {
      const r = await axios.post(`/api/customers/${c.id}/xsiteio/set-active`, { active: target });
      const d = r.data;
      const msg = d.failed > 0
        ? `${d.succeeded}/${d.total} succeeded · ${d.failed} failed. See DevTools for per-site errors.`
        : `${d.succeeded}/${d.total} sites ${target ? 'activated' : 'deactivated'}.`;
      alert(msg);
      // Force a list refresh so the cell re-renders with new counts.
      window.location.reload();
    } catch (err) {
      alert(`Failed: ${err.response?.data?.error || err.message}`);
    } finally {
      setBusy(false);
    }
  }

  return (
    <span
      className={`xsiteio-toggle is-${state}${busy ? ' is-busy' : ''}`}
      onClick={handleClick}
      title={`Click to ${state === 'active' ? 'deactivate' : 'activate'} all ${linked} linked site${linked === 1 ? '' : 's'} in xsiteio`}
      role="button"
      tabIndex={0}
    >
      <span className="xsiteio-toggle-track">
        <span className="xsiteio-toggle-knob" />
      </span>
      <span className="xsiteio-toggle-label">{label}</span>
    </span>
  );
}
const ALL_KEYS     = COLUMNS.map((c) => c.key);

// QBO_ENABLED gates the entire QuickBooks Online surface on this page.
// Set to false 2026-05-27 — the integration is paused. Flip to true
// when/if it ever comes back; nothing else needs to change here.
const QBO_ENABLED = false;

// Days from today to a renewal date: positive = future, negative = past.
function renewalDays(iso, todayISO) {
  if (!iso) return null;
  const a = new Date(String(iso).slice(0, 10) + 'T00:00:00');
  const b = new Date(todayISO + 'T00:00:00');
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
  return Math.round((a.getTime() - b.getTime()) / 86400000);
}

// One renewal tile per site, styled identically to the Sales Leads
// follow-up cards (same followup-* classes). Clicking opens the parent
// customer record.
function RenewalSiteCard({ site, todayISO, onClick, onRenewed }) {
  const renewal = formatDate(site.renewal_date);
  const days = renewalDays(site.renewal_date, todayISO);
  const overdue = days != null && days < 0;
  const urgency = overdue ? 'overdue' : (days === 0 ? 'today' : 'upcoming');
  const status = deriveRenewalStatus(site.renewal_date, todayISO);
  const statusTone = status === 'Overdue' ? 'red' : status === 'Near' ? 'amber' : 'green';
  const location = [site.city, site.province_state].filter(Boolean).join(', ');
  // Prefer the per-site name (e.g. "Centex Petroleum Yale Road -
  // CEN00180") over the parent customer's company name. For a
  // multi-site head office like Centex Petroleum Head Office every
  // child site would otherwise show the parent company on the tile,
  // which is uninformative. Fall back through company → contact
  // name only when site_name is blank.
  const title = site.site_name
    || site.company
    || [site.first_name, site.last_name].filter(Boolean).join(' ')
    || 'Customer';
  const [marking, setMarking] = useState(false);
  const [markError, setMarkError] = useState(null);

  // One-click renewal. Hits the markRenewed endpoint, which atomically:
  //   - rolls forward subscription modules due within 60 days by 1 yr
  //   - bumps sites.renewal_date by 1 yr
  //   - clears subscriptions.price_increase_email_sent
  //   - writes a Note to the customer's timeline
  // After success, onRenewed() refreshes the parent strip so this
  // card disappears from the current month and moves to next year.
  async function handleMarkRenewed(e) {
    e.stopPropagation();
    if (!window.confirm(
      `Mark "${site.site_name || site.site_number}" renewed?\n\n` +
      `• Subscription rolls forward 1 year\n` +
      `• Site renewal_date moves to ${renewal.replace(/^(\d{4})/, (y) => Number(y) + 1)}\n` +
      `• Price-increase flag clears for next cycle\n` +
      `• A Note is added to the customer's timeline`
    )) return;
    setMarking(true);
    setMarkError(null);
    try {
      await axios.post('/api/subscriptions/mark-renewed', { site_id: site.site_id });
      if (typeof onRenewed === 'function') onRenewed();
    } catch (err) {
      setMarkError(err.response?.data?.error || err.message);
    } finally {
      setMarking(false);
    }
  }

  // Outer is a div (not button) so we can nest a real Mark Renewed
  // button inside without HTML-invalid nesting. role+tabIndex+onKeyDown
  // preserve the keyboard-activate behaviour the old button had.
  return (
    <div
      role="button"
      tabIndex={0}
      className={`followup-card followup-${urgency}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); }
      }}
      style={{ cursor: 'pointer' }}
    >
      <div className="followup-head">
        <div className="followup-title">{title}</div>
        {/* OVERDUE / NEAR / ACTIVE badge stacks with the Mark Renewed
            action button directly underneath. */}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 4 }}>
          {status && <span className={`badge badge-${statusTone}`}>{status}</span>}
          <button
            type="button"
            className="btn-primary btn-sm"
            onClick={handleMarkRenewed}
            disabled={marking}
            title="Roll subscription forward 1 year and write a note"
            style={{
              fontSize: '11px',
              padding: '2px 8px',
              whiteSpace: 'nowrap'
            }}
          >
            {marking ? 'Renewing…' : 'Mark Renewed'}
          </button>
        </div>
      </div>
      {markError && (
        <div style={{ color: 'var(--color-bad, #b91c1c)', fontSize: 'var(--text-small)', marginTop: 4 }}>
          {markError}
        </div>
      )}
      <div className="followup-meta">
        {site.site_name && <span>{site.site_name}</span>}
        {site.site_number && <><span className="sep" /><span className="cell-mono">#{site.site_number}</span></>}
        {site.pos_type && <><span className="sep" /><PosBadge value={site.pos_type} /></>}
        {location && <><span className="sep" />{location}</>}
        {site.fuel_brand && <><span className="sep" />{site.fuel_brand}</>}
      </div>
      <div className="followup-foot">
        <span className={`followup-status followup-status-${urgency}`}>
          {overdue && `${Math.abs(days)} day${Math.abs(days) === 1 ? '' : 's'} overdue`}
          {!overdue && days === 0 && 'Due today'}
          {!overdue && days != null && days > 0 && `In ${days} days`}
        </span>
        <span className="cell-mono followup-date">{renewal}</span>
      </div>
      {/* Per-site annual subscription value -- small line at the
          bottom so the user can see which sites are big-revenue and
          which are small without leaving the strip. Hidden when
          there's no subscription on file (server returns 0). */}
      {Number(site.site_annual_value) > 0 && (
        <div style={{
          marginTop: 'var(--space-1)',
          fontSize: 'var(--text-small)',
          color: 'var(--color-text-dim)',
          fontFamily: 'var(--font-mono)'
        }}>
          ${Math.round(Number(site.site_annual_value)).toLocaleString('en-CA')}/yr
        </div>
      )}
    </div>
  );
}

// 12-month renewal strip across the top of the Customers page. Each
// month gets its own fixed hue so the strip reads as a colour-coded
// calendar; clicking one filters the table to customers renewing that
// month. A multi-site customer counts under every month it renews.
const MONTH_LABELS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const MONTH_HUES = [
  '#dc2626', '#ea580c', '#d97706', '#ca8a04', '#65a30d', '#16a34a',
  '#0d9488', '#0891b2', '#2563eb', '#4f46e5', '#7c3aed', '#c026d3'
];

// A customer's renewal months (1-12). Prefers the server-provided
// renewal_months array (distinct months across all its sites); falls
// back to the single renewal_date so the strip still works if the API
// hasn't been redeployed with renewal_months yet.
function customerRenewalMonths(c) {
  if (Array.isArray(c.renewal_months)) {
    return c.renewal_months.filter((m) => m >= 1 && m <= 12);
  }
  const iso = c.renewal_date || c.nearest_renewal_date;
  if (iso && /^\d{4}-\d{2}/.test(String(iso))) {
    const m = parseInt(String(iso).slice(5, 7), 10);
    if (m >= 1 && m <= 12) return [m];
  }
  return [];
}

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  // "Insights" panel toggle (Dave 2026-06-12). Hides the renewal
  // month-strip + the three action-queue cards (Overdue / Renewing /
  // Dark) + the color legend behind a single button so the table
  // floats up near the top of the page. Persists per-browser so
  // Dave's preference sticks across reloads.
  const INSIGHTS_KEY = 'customers.showInsights';
  const [showInsights, setShowInsights] = useState(() => {
    try { return localStorage.getItem(INSIGHTS_KEY) === '1'; } catch (_) { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem(INSIGHTS_KEY, showInsights ? '1' : '0'); } catch (_) { /* noop */ }
  }, [showInsights]);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  // Sites-missing-subscription panel: a tiny banner shows the count
  // even before the user expands the list, so students see the
  // workload at a glance. The list itself is lazy-fetched.
  const [missingCount, setMissingCount]       = useState(null);
  const [missingOpen, setMissingOpen]         = useState(false);
  const [missingSites, setMissingSites]       = useState(null);
  const [missingLoading, setMissingLoading]   = useState(false);
  const [missingError, setMissingError]       = useState(null);
  // Sort + filter + selection state for the missing-subs table.
  // sortKey/sortDir control the column ordering; click a header to
  // toggle. selectedIds is a Set of site_ids the student has ticked.
  const [missingSortKey, setMissingSortKey]   = useState('company');
  const [missingSortDir, setMissingSortDir]   = useState('asc');
  const [missingFilters, setMissingFilters]   = useState({ province: '', fuel_brand: '', pos_type: '', q: '' });
  const [missingSelected, setMissingSelected] = useState(new Set());
  // Action-queue filter: null | 'overdue' | 'renewing30' | 'dark90'.
  // Click a card to scope the table; click again to clear.
  const [actionFilter, setActionFilter] = useState(null);
  // Highlight-colour filter (null = all). When set, table scopes to
  // customers whose effective highlight (manual highlight_color OR
  // auto price_increase_sent → 'orange') matches.
  const [colorFilter, setColorFilter] = useState(null);
  // Column sort state. Default: company A→Z. Click a header to switch
  // column (resets to asc for that column); click the active header
  // again to toggle asc/desc. null = no sort, fall back to API order.
  const [sortBy, setSortBy] = useState('company');
  const [sortDir, setSortDir] = useState('asc');
  // 3-way filter on customer activity:
  //   'active'   → only customers with at least one Active site (default)
  //   'all'      → both active and inactive-only customers
  //   'inactive' → only customers whose every site is Inactive
  // Replaces the earlier boolean toggle so Dave can switch between an
  // active-only view, a combined view, and an inactive-only view from
  // a single segmented control near the search bar.
  const [customerStatusFilter, setCustomerStatusFilter] = useState('active');
  // Active renewal-month filter (1-12). Defaults to null (no filter)
  // so the full customer list shows on load - Dave 2026-05-30. Click
  // any month in the strip below to scope the table to that month.
  const [monthFilter, setMonthFilter] = useState(null);
  // Per-site renewal rows (for the strip counts + month tiles).
  const [siteRenewalsRows, setSiteRenewalsRows] = useState([]);
  // Multi-select state for Merge to Parent. Set<number> of customer
  // ids — kept as a Set so toggling is O(1) and the "select all" /
  // "deselect all" toolbar can swap the whole reference at once.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [mergeOpen, setMergeOpen] = useState(false);
  const [bulkPullBusy, setBulkPullBusy] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  // QBO sync state. syncing = request in flight; syncResult = the most
  // recent { created, updated, skipped, total } summary from the server
  // (shown inline under the toolbar until dismissed or replaced).
  const [syncing, setSyncing] = useState(false);
  // xsiteio manual sync state. Separate from QBO `syncing` so the
  // two buttons can be clicked independently (and one running doesn't
  // disable the other). syncBanner holds the last-result message that
  // appears briefly next to the button after a sync completes.
  const [syncingXsiteio, setSyncingXsiteio] = useState(false);
  const [xsiteioBanner, setXsiteioBanner] = useState(null);  // { ok, text } | null
  // Per-site detail returned by the sync -- specifically the list of
  // xsiteio sites that didn't match (so Dave knows which Site IDs to
  // fix in xsiteio). Cleared when the banner is dismissed.
  const [xsiteioUnmatched, setXsiteioUnmatched] = useState([]);
  const [xsiteioUnmatchedOpen, setXsiteioUnmatchedOpen] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [syncError, setSyncError] = useState(null);
  const navigate = useNavigate();

  function toggleSelected(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelectedIds(new Set()); }
  const {
    visibleKeys, setVisibleKeys,
    layouts, activeName, setActiveName,
    saveLayout, deleteLayout
  } = useColumnPreferences('customers', DEFAULT_KEYS, ALL_KEYS);

  const {
    filters, setFilter, clearFilter, clearAll,
    activeCount, valuesByKey, applyFilters
  } = useColumnFilters(customers, COLUMNS);

  // Loader factored out so the merge dialog can call it on completion
  // to refresh the list without a full page reload.
  async function load({ keepSelection = false } = {}) {
    setLoading(true);
    try {
      const res = await axios.get('/api/customers');
      setCustomers(res.data.customers || []);
      setError(null);
      if (!keepSelection) setSelectedIds(new Set());
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }

  // Extracted so the Mark-Renewed action on a card can call it after
  // the API succeeds, and the strip count + cards refresh.
  async function reloadSiteRenewals() {
    try {
      const res = await axios.get('/api/customers/site-renewals');
      setSiteRenewalsRows(res.data.sites || []);
    } catch (_) { setSiteRenewalsRows([]); }
  }

  // Sites-missing-subscription loader. Always fetches the count on
  // mount (so the banner can render); the full list is loaded the
  // first time the user opens the panel.
  async function loadMissingSites() {
    setMissingLoading(true);
    setMissingError(null);
    try {
      const r = await axios.get('/api/customers/missing-subscriptions');
      setMissingSites(r.data?.sites || []);
      setMissingCount(r.data?.count ?? (r.data?.sites?.length || 0));
    } catch (e) {
      setMissingError(e.response?.data?.error || e.message);
    } finally {
      setMissingLoading(false);
    }
  }

  useEffect(() => {
    load();
    reloadSiteRenewals();
    loadMissingSites();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Manual xsiteio activity sync. Hits POST /api/xsiteio/sync (same
  // code path the nightly cron will use). Synchronous: ~5s round trip.
  // After it returns we reload the customers list so the new "Last
  // Data Update" column reflects fresh timestamps. Banner shows the
  // count summary for a few seconds; failures stay until next click.
  async function runXsiteioSync() {
    if (syncingXsiteio) return;
    setSyncingXsiteio(true);
    setXsiteioBanner(null);
    try {
      const r = await axios.post('/api/xsiteio/sync');
      const {
        matched, updated, unchanged, unmatched, missing_unique_id,
        unmatched_rows = [], elapsed_ms
      } = r.data || {};
      // Spotlight missing_unique_id when it's non-zero — that's the
      // data-discipline signal Dave wants to see ("which xsiteio sites
      // need a Site ID filled in").
      const tail = missing_unique_id
        ? ` · ${missing_unique_id} missing Site ID in xsiteio`
        : '';
      setXsiteioBanner({
        ok: true,
        text: `${matched} matched · ${updated} updated · ${unchanged} unchanged · ${unmatched} unmatched${tail} (${Math.round((elapsed_ms || 0) / 100) / 10}s)`
      });
      setXsiteioUnmatched(unmatched_rows);
      // Auto-open the unmatched panel if there's anything to fix --
      // less clicking for Dave, and the moment after sync is when
      // he's most likely to act on the list.
      setXsiteioUnmatchedOpen(unmatched_rows.length > 0);
      await load({ keepSelection: true });
      // Auto-dismiss success banner after 8s so the header doesn't
      // stay cluttered. Errors stay until the next click.
      setTimeout(() => {
        setXsiteioBanner((b) => (b && b.ok ? null : b));
      }, 8000);
    } catch (err) {
      setXsiteioBanner({
        ok: false,
        text: err.response?.data?.error || err.message || 'sync failed'
      });
    } finally {
      setSyncingXsiteio(false);
    }
  }

  // Pulls every customer from the connected QBO company and upserts
  // them into contacts. Backed by POST /api/qbo/sync-customers.
  async function syncFromQbo() {
    setSyncing(true);
    setSyncError(null);
    setSyncResult(null);
    try {
      const r = await axios.post('/api/qbo/sync-customers');
      setSyncResult(r.data);
      // Refresh the list so newly imported QBO customers show up.
      await load({ keepSelection: true });
    } catch (err) {
      setSyncError(err.response?.data?.error || err.message);
    } finally {
      setSyncing(false);
    }
  }

  // ─── Action-queue predicates (Dave 2026-05-31) ─────────────────────
  // Three buckets driven by existing fields. Computed once per render
  // and used both for the cards' counts and for the table filter.
  const ACTION_PREDICATES = useMemo(() => {
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();
    const dayMs = 86400000;
    return {
      // next_contact is set AND in the past
      overdue: (c) => {
        if (!c.next_contact) return false;
        const t = new Date(c.next_contact).getTime();
        return Number.isFinite(t) && t < todayMs;
      },
      // nearest_renewal_date within the next 30 days (inclusive of today)
      renewing30: (c) => {
        if (!c.nearest_renewal_date) return false;
        const t = new Date(c.nearest_renewal_date).getTime();
        if (!Number.isFinite(t)) return false;
        const days = (t - todayMs) / dayMs;
        return days >= 0 && days <= 30;
      },
      // last_contact is null OR more than 90 days ago
      dark90: (c) => {
        if (!c.last_contact) return true;
        const t = new Date(c.last_contact).getTime();
        if (!Number.isFinite(t)) return true;
        return (todayMs - t) / dayMs > 90;
      },
      // Price-Increase Action Required — mirrors the dashboard panel.
      // Three criteria (per Dave 2026-06-03):
      //   1. Renewal date in current month or next 2 months
      //   2. price_increase_email_sent never set on any of their sites
      //   3. Customer has been with us at least 1 year (install_date)
      //   Excludes fully-inactive customers (active_site_count = 0).
      priceIncreaseAction: (c) => {
        if (c.price_increase_sent) return false;
        if ((Number(c.active_site_count) || 0) === 0) return false;
        if (!c.nearest_renewal_date) return false;
        const renewalT = new Date(c.nearest_renewal_date).getTime();
        if (!Number.isFinite(renewalT)) return false;
        const monthStart = new Date(); monthStart.setDate(1); monthStart.setHours(0,0,0,0);
        const cutoffEnd  = new Date(monthStart); cutoffEnd.setMonth(cutoffEnd.getMonth() + 3);
        if (renewalT < monthStart.getTime() || renewalT >= cutoffEnd.getTime()) return false;
        if (!c.install_date) return false;
        const installT = new Date(c.install_date).getTime();
        if (!Number.isFinite(installT)) return false;
        return installT <= todayMs - 365 * dayMs;
      }
    };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const byQuery = !q ? customers : customers.filter((c) => {
      const haystack = [
        c.company, c.first_name, c.last_name, c.position, c.site_number, c.pos_type,
        c.city, c.province_state, c.phone_main, c.phone_cell, c.email1, c.fuel_brand,
        formatDate(c.install_date), formatDate(c.renewal_date),
        // all_sites_search is a concatenation of every site_number +
        // site_name for this customer (server-built). Means a search
        // for any site number on a multi-site chain (e.g. cen00123
        // under Centex Petroleum Head Office) finds the parent.
        c.all_sites_search
      ].map((v) => (v == null ? '' : String(v).toLowerCase())).join(' | ');
      return haystack.includes(q);
    });
    const byMonth = monthFilter == null
      ? byQuery
      : byQuery.filter((c) => customerRenewalMonths(c).includes(monthFilter));
    const byAction = actionFilter && ACTION_PREDICATES[actionFilter]
      ? byMonth.filter(ACTION_PREDICATES[actionFilter])
      : byMonth;
    const byColor = colorFilter
      ? byAction.filter((c) => effectiveColor(c) === colorFilter)
      : byAction;
    // 3-way activity filter (see customerStatusFilter state). Helpers:
    //   isInactiveOnly = site_count>0 AND active_site_count===0
    //   isFreshNoSites = site_count===0
    //   isActive       = active_site_count>0
    // 'active' keeps active + fresh customers (the default working set).
    // 'inactive' keeps only the inactive-only bucket.
    // 'all' keeps everything.
    const byActivity = (() => {
      if (customerStatusFilter === 'all') return byColor;
      return byColor.filter((c) => {
        const total  = Number(c.site_count)        || 0;
        const active = Number(c.active_site_count) || 0;
        const isInactiveOnly = total > 0 && active === 0;
        if (customerStatusFilter === 'inactive') return isInactiveOnly;
        return !isInactiveOnly; // 'active' default
      });
    })();
    const filteredRows = applyFilters(byActivity);
    // Column sort. nullable values sink to the bottom in either
    // direction so empty cells don't crowd the top.
    if (!sortBy) return filteredRows;
    const col = COLUMNS.find((co) => co.key === sortBy);
    const getValue = (row) => {
      if (col && typeof col.filterValue === 'function') return col.filterValue(row);
      return row[sortBy];
    };
    const sign = sortDir === 'desc' ? -1 : 1;
    return [...filteredRows].sort((a, b) => {
      const av = getValue(a);
      const bv = getValue(b);
      const aEmpty = av == null || av === '';
      const bEmpty = bv == null || bv === '';
      if (aEmpty && bEmpty) return 0;
      if (aEmpty) return 1;
      if (bEmpty) return -1;
      // Numeric vs string compare
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * sign;
      }
      // Date strings (YYYY-MM-DD) compare correctly via string compare,
      // so falls into the default branch below.
      const as = String(av).toLowerCase();
      const bs = String(bv).toLowerCase();
      if (as < bs) return -1 * sign;
      if (as > bs) return  1 * sign;
      return 0;
    });
  }, [customers, query, applyFilters, monthFilter, actionFilter, ACTION_PREDICATES, colorFilter, customerStatusFilter, sortBy, sortDir]);

  // Counts for the segmented-control labels so Dave can see the size
  // of each bucket without flipping through them.
  const activityCounts = useMemo(() => {
    let active = 0, inactiveOnly = 0;
    for (const c of customers) {
      const total = Number(c.site_count) || 0;
      const act   = Number(c.active_site_count) || 0;
      if (total > 0 && act === 0) inactiveOnly++;
      else active++;
    }
    return { active, inactiveOnly, all: customers.length };
  }, [customers]);

  // Tally customers by effective highlight color so the chip strip can
  // show "Orange (71)", "Green (5)", etc. Only colors with ≥1 hit are
  // rendered so the strip stays compact.
  const colorCounts = useMemo(() => {
    const tally = {};
    for (const c of customers) {
      const k = effectiveColor(c);
      if (!k) continue;
      tally[k] = (tally[k] || 0) + 1;
    }
    return tally;
  }, [customers]);

  // Counts for the action-queue cards — applied to the FULL customer
  // list (not the search/month-filtered set) so the numbers stay stable
  // as Dave types in the search box.
  const actionCounts = useMemo(() => ({
    overdue:             customers.filter(ACTION_PREDICATES.overdue).length,
    renewing30:          customers.filter(ACTION_PREDICATES.renewing30).length,
    dark90:              customers.filter(ACTION_PREDICATES.dark90).length,
    priceIncreaseAction: customers.filter(ACTION_PREDICATES.priceIncreaseAction).length
  }), [customers, ACTION_PREDICATES]);

  // Count of SITES renewing in each month (1-12) for the strip badges --
  // consistent with the per-site tiles shown below the strip.
  const monthCounts = useMemo(() => {
    const counts = new Array(13).fill(0);
    for (const s of siteRenewalsRows) {
      if (s.renewal_month >= 1 && s.renewal_month <= 12) counts[s.renewal_month]++;
    }
    return counts;
  }, [siteRenewalsRows]);

  // Per-month total annual subscription value (any year). Drives the
  // dollar pill on each Jan/Feb/.../Dec strip badge so the strip is
  // an at-a-glance revenue forecast: "Jan $42K" tells Dave the size
  // of January's book regardless of how many sites it represents.
  const monthRevenue = useMemo(() => {
    const totals = new Array(13).fill(0);
    for (const s of siteRenewalsRows) {
      if (s.renewal_month >= 1 && s.renewal_month <= 12) {
        totals[s.renewal_month] += Number(s.site_annual_value) || 0;
      }
    }
    return totals;
  }, [siteRenewalsRows]);

  // Compact money for the small badge pills. $42K reads at a glance;
  // $42,123.50 would blow the chip's width.
  function fmtCompactMoney(n) {
    if (!n || n <= 0)         return '$0';
    if (n < 1000)             return `$${Math.round(n)}`;
    if (n < 1_000_000)        return `$${Math.round(n / 1000)}K`;
    return `$${(n / 1_000_000).toFixed(1)}M`;
  }

  // Computed up here (above monthTiles) so the useMemo at line ~735
  // can reference it -- a previous edit declared it below and hit
  // "Cannot access 'todayISO' before initialization", blanking the
  // page. Date-only ISO so renewalDays() compares apples-to-apples.
  const todayISO = new Date().toISOString().slice(0, 10);

  // Sites renewing in the selected month, soonest first -> rendered as tiles.
  // monthAllSites = every site renewing in the month (used for the
  // subtitle count).
  // monthTiles    = only the actionable subset -- overdue OR within 5
  //                 days. Per Dave 2026-06-03: the renewal panel is a
  //                 *reminder*, not a forecast. Anything further out
  //                 than 5 days is visible via the strip count but
  //                 hidden from the cards so the screen stays focused
  //                 on what actually needs work today.
  const monthAllSites = useMemo(() => {
    if (monthFilter == null) return [];
    return siteRenewalsRows
      .filter((s) => s.renewal_month === monthFilter)
      .sort((a, b) => String(a.renewal_date).localeCompare(String(b.renewal_date)));
  }, [siteRenewalsRows, monthFilter]);
  // Per Dave 2026-06-03 (revisited): when a month chip is clicked the
  // user wants to see EVERY site renewing in that month, regardless
  // of how far out the date is. Earlier we filtered to overdue/within-
  // 5-days because the panel was framed as a "what needs work today"
  // reminder, but the chip count and the visible tile count have to
  // match — otherwise "16" in the chip with only 1 tile below it is
  // confusing. Tiles = monthAllSites verbatim now.
  // Per Dave 2026-06-03 (third revision): cards should only show
  // sites due in the next 5 days. Overdue sites are intentionally
  // hidden -- the 128-days-overdue cases were drowning out the
  // actually-imminent ones. Overdue still appears in the customer
  // table below the strip, so they're not lost.
  const monthTiles = useMemo(() => {
    return monthAllSites.filter((s) => {
      const days = renewalDays(s.renewal_date, todayISO);
      if (days == null) return false;
      return days >= 0 && days <= 5;
    });
  }, [monthAllSites, todayISO]);

  // Summary numbers for the month-tiles header. Dave asked for the
  // TOTAL annual value of the month (not just the overdue/at-risk
  // subset) so he can see what the month is worth at a glance.
  // at-risk = overdue OR due within 5 days, mirroring the visual
  // cue on each site card.
  const monthSummary = useMemo(() => {
    // Two bugs fixed:
    //  1. Iterate monthAllSites (every site renewing in this calendar
    //     month, any year) instead of monthTiles. monthTiles is now
    //     filtered to the actionable subset, so summing it would
    //     under-count "what's January worth across all years" -- the
    //     forecasting view Dave actually wants.
    //  2. The server column is `site_annual_value`, not
    //     `annual_value`. The old key always returned undefined ->
    //     coerced to 0, so the total was always 0 regardless of which
    //     array we summed.
    if (monthAllSites.length === 0) return { totalAnnual: 0, atRisk: 0 };
    const today = new Date(); today.setHours(0, 0, 0, 0);
    let totalAnnual = 0;
    let atRisk = 0;
    for (const s of monthAllSites) {
      totalAnnual += Number(s.site_annual_value) || 0;
      const iso = s.renewal_date ? String(s.renewal_date).slice(0, 10) : null;
      if (iso) {
        const d = new Date(iso + 'T00:00:00');
        if (!Number.isNaN(d.getTime())) {
          const days = Math.round((d - today) / 86400000);
          if (days <= 5) atRisk++;
        }
      }
    }
    return { totalAnnual, atRisk };
  }, [monthAllSites]);

  // Total sites across ALL customers (sum of each customer's site_count),
  // shown beside the company count so Dave can see the fleet size at a glance.
  const totalSites = useMemo(
    () => customers.reduce((sum, c) => sum + (Number(c.site_count) || 0), 0),
    [customers]
  );

  // Filtered sites: sum of site_count across the currently-filtered rows,
  // shown beside Clear Filters when a filter is active so Dave can see
  // how many physical locations the narrowed list represents (e.g. a
  // 'Petro Canada' filter might match 12 companies but 95 locations).
  const filteredSites = useMemo(
    () => filtered.reduce((sum, c) => sum + (Number(c.site_count) || 0), 0),
    [filtered]
  );

  const visibleColumns = COLUMNS.filter((c) => visibleKeys.includes(c.key));

  return (
    <section className="page">
      <div className="page-header">
        <h2>Customers</h2>
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
          {/* xsiteio manual sync. Lives here so it's reachable from
              the page that shows the Last Pricebook Update column -- you
              fix a site number in xsiteio, click this, and the column
              updates. ~5s round trip; banner shows the summary. */}
          {xsiteioBanner && (
            <span
              className="cell-mono"
              style={{
                fontSize: 11,
                marginRight: 4,
                color: xsiteioBanner.ok ? 'var(--color-good)' : 'var(--color-bad)',
                maxWidth: 380,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
              title={xsiteioBanner.text}
            >
              {xsiteioBanner.ok ? '✓' : '✕'} {xsiteioBanner.text}
            </span>
          )}
          <button
            type="button"
            className="btn-secondary"
            onClick={runXsiteioSync}
            disabled={syncingXsiteio}
            title="Pull fresh activity timestamps from xsiteio"
          >
            {syncingXsiteio ? 'Syncing xsiteio…' : 'Sync xsiteio'}
          </button>
          {QBO_ENABLED && (
            <button type="button" className="btn-secondary" onClick={syncFromQbo} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync from QuickBooks'}
            </button>
          )}
          <button type="button" className="btn-secondary" onClick={() => setImportOpen(true)}>
            Import from Back Office
          </button>
          <Link to="/customers/import" className="btn-secondary">Import CSV</Link>
          <button
            type="button"
            className="btn-secondary"
            onClick={async () => {
              try {
                const res = await axios.get('/api/sites/export', { responseType: 'blob' });
                const url = URL.createObjectURL(res.data);
                const a = document.createElement('a');
                a.href = url;
                a.download = `sites-export-${new Date().toISOString().slice(0, 10)}.csv`;
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                URL.revokeObjectURL(url);
              } catch (e) {
                alert('Site export failed: ' + (e.response?.status || e.message));
              }
            }}
            title="Download every site as a CSV (master list)"
          >
            Export Sites CSV
          </button>
          <Link to="/customers/new" className="btn-primary">+ Add New Customer</Link>
        </div>
      </div>

      {/* xsiteio unmatched-sites panel. Populated by the Sync xsiteio
          button. Auto-opens after a sync if there's anything to fix;
          closable. Each row is an xsiteio site we couldn't tie to a
          local site -- so the list IS Dave's hit-list of "go fix the
          Site ID in xsiteio." */}
      {xsiteioUnmatched.length > 0 && xsiteioUnmatchedOpen && (
        <div style={{
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md, 6px)',
          padding: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          boxShadow: 'var(--shadow-sm)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', marginBottom: 'var(--space-2)' }}>
            <strong style={{ fontSize: 'var(--text-body)' }}>
              {xsiteioUnmatched.length} xsiteio site{xsiteioUnmatched.length === 1 ? '' : 's'} couldn't be matched
            </strong>
            <span className="cell-mono" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
              fix the Site ID field on these in xsiteio, then click Sync again
            </span>
            <button
              type="button"
              className="btn-secondary btn-sm"
              style={{ marginLeft: 'auto' }}
              onClick={() => setXsiteioUnmatchedOpen(false)}
            >
              Hide
            </button>
          </div>
          <div style={{ maxHeight: 240, overflowY: 'auto' }}>
            <table className="data-table" style={{ width: '100%' }}>
              <thead>
                <tr>
                  <th>xsiteio Site Name</th>
                  <th>Site ID (xsiteio)</th>
                  <th>Why it didn't match</th>
                </tr>
              </thead>
              <tbody>
                {xsiteioUnmatched.slice(0, 50).map((row) => (
                  <tr key={row.xsiteio_site_id}>
                    <td>{row.site_name || `(no name, id ${row.xsiteio_site_id})`}</td>
                    <td className="cell-mono">{row.site_unique_id || <span className="cell-muted">— empty —</span>}</td>
                    <td className="cell-mono" style={{ fontSize: 11, color: 'var(--color-text-dim)' }}>
                      {row.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {/* Re-open chip for the xsiteio unmatched panel after Dave hides
          it. Tiny — it's just a "show again" affordance so the data
          isn't gone after one accidental click. */}
      {xsiteioUnmatched.length > 0 && !xsiteioUnmatchedOpen && (
        <div style={{ marginBottom: 'var(--space-3)' }}>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setXsiteioUnmatchedOpen(true)}
          >
            Show {xsiteioUnmatched.length} unmatched xsiteio site{xsiteioUnmatched.length === 1 ? '' : 's'}
          </button>
        </div>
      )}

      {/* Sites-missing-subscription banner. Visible whenever there's a
          non-zero gap. Click "Show list" to expand the table; click any
          row to open the customer detail so the student can fill the
          subscription. Click "Reload" after they save to see progress. */}
      {missingCount != null && missingCount > 0 && (
        <div style={{
          background: 'var(--color-warn-bg, #fef3c7)',
          border: '1px solid var(--color-warn-border, #fcd34d)',
          borderRadius: 'var(--radius-md, 6px)',
          padding: 'var(--space-3)',
          marginBottom: 'var(--space-3)',
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--space-3)',
          flexWrap: 'wrap'
        }}>
          <span style={{ fontSize: 18 }} aria-hidden>⚠</span>
          <div style={{ flex: 1, minWidth: 240 }}>
            <strong>{missingCount} site{missingCount === 1 ? '' : 's'}</strong>
            {' '}need subscription data added before they show up in revenue totals.
          </div>
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => {
              if (!missingOpen && missingSites == null) loadMissingSites();
              setMissingOpen((v) => !v);
            }}
          >
            {missingOpen ? 'Hide list' : 'Show list'}
          </button>
          {missingOpen && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={loadMissingSites}
              disabled={missingLoading}
            >
              {missingLoading ? 'Reloading…' : 'Reload'}
            </button>
          )}
        </div>
      )}

      {missingOpen && (() => {
        // Distinct values for the dropdown filters. Computed from
        // missingSites so the dropdowns only offer values that exist
        // (no empty Saskatchewan option when there are no SK sites).
        const distinct = (key) => Array.from(new Set(
          (missingSites || []).map((s) => s[key]).filter(Boolean)
        )).sort();
        const provinces = distinct('province_state');
        const brands    = distinct('fuel_brand');
        const posTypes  = distinct('pos_type');

        // Apply filters + sort to the raw list.
        const filtered = (missingSites || []).filter((s) => {
          if (missingFilters.province   && s.province_state !== missingFilters.province)   return false;
          if (missingFilters.fuel_brand && s.fuel_brand     !== missingFilters.fuel_brand) return false;
          if (missingFilters.pos_type   && s.pos_type       !== missingFilters.pos_type)   return false;
          if (missingFilters.q) {
            const q = missingFilters.q.toLowerCase();
            const hay = [s.company, s.site_name, s.site_number, s.city]
              .map((v) => (v || '').toLowerCase()).join(' | ');
            if (!hay.includes(q)) return false;
          }
          return true;
        });

        // Sort. Strings comparable directly; null/empty sinks to end.
        const sign = missingSortDir === 'desc' ? -1 : 1;
        const sortField = missingSortKey === 'company' ? 'company' : missingSortKey;
        const sorted = [...filtered].sort((a, b) => {
          const av = a[sortField] ?? '';
          const bv = b[sortField] ?? '';
          if (!av && !bv) return 0;
          if (!av) return 1;
          if (!bv) return -1;
          return String(av).localeCompare(String(bv)) * sign;
        });

        function toggleSort(key) {
          if (missingSortKey === key) {
            setMissingSortDir(d => d === 'asc' ? 'desc' : 'asc');
          } else {
            setMissingSortKey(key);
            setMissingSortDir('asc');
          }
        }
        const arrow = (key) => missingSortKey === key
          ? (missingSortDir === 'asc' ? ' ▴' : ' ▾')
          : '';

        // Selection helpers.
        const allSelected = sorted.length > 0 && sorted.every((s) => missingSelected.has(s.site_id));
        function toggleAll() {
          setMissingSelected(prev => {
            if (allSelected) {
              const next = new Set(prev);
              sorted.forEach((s) => next.delete(s.site_id));
              return next;
            }
            const next = new Set(prev);
            sorted.forEach((s) => next.add(s.site_id));
            return next;
          });
        }
        function toggleOne(id) {
          setMissingSelected(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          });
        }

        // CSV export of the current selection (or all rows if no
        // selection). Helps students track progress offline.
        function exportCsv() {
          const rows = missingSelected.size > 0
            ? sorted.filter((s) => missingSelected.has(s.site_id))
            : sorted;
          const headers = ['Customer', 'Site Name', 'Site #', 'City', 'Province', 'Fuel Brand', 'POS', 'Install Date'];
          const escape = (v) => {
            if (v == null) return '';
            const s = String(v);
            return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
          };
          const lines = [headers.join(',')];
          for (const s of rows) {
            lines.push([
              s.company, s.site_name, s.site_number, s.city,
              s.province_state, s.fuel_brand, s.pos_type,
              s.install_date ? String(s.install_date).slice(0, 10) : ''
            ].map(escape).join(','));
          }
          const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `sites-missing-subscriptions-${new Date().toISOString().slice(0,10)}.csv`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
        }

        return (
          <div className="panel" style={{ padding: 'var(--space-3)', marginBottom: 'var(--space-3)', overflow: 'auto' }}>
            <p className="result-count" style={{ marginBottom: 'var(--space-2)' }}>
              Sort by clicking a column header. Filter with the dropdowns. Tick checkboxes to mark which sites you've handled (selection survives sort/filter changes), then <strong>Export CSV</strong> to take an offline checklist.
            </p>

            {/* Filter row */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              marginBottom: 'var(--space-3)'
            }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="result-count">Province</span>
                <select value={missingFilters.province} onChange={(e) => setMissingFilters(f => ({...f, province: e.target.value}))}>
                  <option value="">All</option>
                  {provinces.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="result-count">Fuel Brand</span>
                <select value={missingFilters.fuel_brand} onChange={(e) => setMissingFilters(f => ({...f, fuel_brand: e.target.value}))}>
                  <option value="">All</option>
                  {brands.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span className="result-count">POS Type</span>
                <select value={missingFilters.pos_type} onChange={(e) => setMissingFilters(f => ({...f, pos_type: e.target.value}))}>
                  <option value="">All</option>
                  {posTypes.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 2, flex: '1 1 200px', minWidth: 180 }}>
                <span className="result-count">Search</span>
                <input
                  type="search"
                  placeholder="Customer, site, city…"
                  value={missingFilters.q}
                  onChange={(e) => setMissingFilters(f => ({...f, q: e.target.value}))}
                />
              </label>
              {(missingFilters.province || missingFilters.fuel_brand || missingFilters.pos_type || missingFilters.q) && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setMissingFilters({ province: '', fuel_brand: '', pos_type: '', q: '' })}
                >
                  Clear filters
                </button>
              )}
            </div>

            {/* Selection + export bar */}
            <div style={{
              display: 'flex',
              gap: 'var(--space-3)',
              alignItems: 'center',
              marginBottom: 'var(--space-2)',
              flexWrap: 'wrap'
            }}>
              <span className="result-count">
                {sorted.length} site{sorted.length === 1 ? '' : 's'} after filters
                {missingSelected.size > 0 && (
                  <> · <strong>{missingSelected.size} selected</strong></>
                )}
              </span>
              {missingSelected.size > 0 && (
                <button
                  type="button"
                  className="btn-secondary btn-sm"
                  onClick={() => setMissingSelected(new Set())}
                >
                  Clear selection
                </button>
              )}
              <button
                type="button"
                className="btn-secondary btn-sm"
                onClick={exportCsv}
                disabled={sorted.length === 0}
                title={missingSelected.size > 0 ? 'Export selected rows' : 'Export all filtered rows'}
              >
                Export {missingSelected.size > 0 ? 'selected' : 'visible'} as CSV
              </button>
            </div>

            {missingLoading && missingSites == null && <p>Loading…</p>}
            {missingError && <p className="error">{missingError}</p>}
            {missingSites != null && missingSites.length === 0 && (
              <p className="panel-empty">Nothing to fix — every Active site has subscription data.</p>
            )}
            {sorted.length === 0 && missingSites != null && missingSites.length > 0 && (
              <p className="panel-empty">No sites match the current filters.</p>
            )}
            {sorted.length > 0 && (
              <table className="data-table" style={{ width: '100%' }}>
                <thead>
                  <tr>
                    <th style={{ width: 36, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={toggleAll}
                        title={allSelected ? 'Deselect all visible' : 'Select all visible'}
                      />
                    </th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('company')}>Customer{arrow('company')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('site_name')}>Site Name{arrow('site_name')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('site_number')}>Site #{arrow('site_number')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('city')}>City{arrow('city')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('province_state')}>Prov{arrow('province_state')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('fuel_brand')}>Fuel Brand{arrow('fuel_brand')}</th>
                    <th style={{ textAlign: 'left',  cursor: 'pointer' }} onClick={() => toggleSort('pos_type')}>POS{arrow('pos_type')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((s) => {
                    const checked = missingSelected.has(s.site_id);
                    return (
                      <tr
                        key={s.site_id}
                        style={{
                          cursor: 'pointer',
                          background: checked ? 'var(--color-accent-soft, #cffafe)' : undefined
                        }}
                        onClick={() => navigate(`/customers/${s.customer_id}`)}
                        title={`Open ${s.company || '(unknown customer)'}`}
                      >
                        <td onClick={(e) => e.stopPropagation()} style={{ textAlign: 'center' }}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleOne(s.site_id)}
                          />
                        </td>
                        <td>
                          <Link to={`/customers/${s.customer_id}`}
                                onClick={(e) => e.stopPropagation()}
                                style={{ fontWeight: 'var(--font-semibold)', color: 'var(--color-text)', textDecoration: 'none' }}>
                            {s.company || `Customer #${s.customer_id}`}
                          </Link>
                        </td>
                        <td>{s.site_name || '—'}</td>
                        <td className="cell-mono">{s.site_number || '—'}</td>
                        <td>{s.city || '—'}</td>
                        <td>{s.province_state || '—'}</td>
                        <td>{s.fuel_brand || '—'}</td>
                        <td>{s.pos_type || '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })()}

      {QBO_ENABLED && syncError && (
        <p className="error">
          QuickBooks sync failed: {syncError}
          <button
            type="button"
            className="btn-secondary btn-sm"
            style={{ marginLeft: 'var(--space-3)' }}
            onClick={() => setSyncError(null)}
          >
            Dismiss
          </button>
        </p>
      )}
      {QBO_ENABLED && syncResult && (
        <div className="settings-status-row" style={{ marginBottom: 'var(--space-3)' }}>
          <span className="badge badge-green">QBO SYNC</span>
          <span className="form-hint">
            {syncResult.created} created, {syncResult.updated} updated
            {syncResult.skipped > 0 && <>, {syncResult.skipped} skipped</>}
            {' '}({syncResult.total} processed)
          </span>
          <button
            type="button"
            className="btn-secondary btn-sm"
            style={{ marginLeft: 'auto' }}
            onClick={() => setSyncResult(null)}
          >
            Dismiss
          </button>
        </div>
      )}

      <RecentImportBanner recordType="customer" onUndone={() => load()} />

      {/* Insights toggle. Reveals/hides the month strip, action-queue
          cards, and color legend so the table sits closer to the top of
          the page by default. Dave 2026-06-12. Preference persists in
          localStorage (INSIGHTS_KEY). */}
      <div style={{
        display: 'flex',
        justifyContent: 'flex-end',
        margin: 'var(--space-2) 0 var(--space-2)'
      }}>
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={() => setShowInsights((v) => !v)}
          title={showInsights ? 'Hide month strip, action cards, and legend' : 'Show month strip, action cards, and legend'}
          style={{
            color: 'var(--color-text-dim)',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '4px 8px',
            fontSize: 'var(--text-small)',
            fontWeight: 'var(--font-semibold)'
          }}
        >
          {showInsights ? '▴ Hide insights' : '▾ Show insights'}
        </button>
      </div>

      {/* Renewals-by-month strip. 12 colour-coded chips; click one to
          filter the table to customers renewing that month, click again
          to clear. Counts reflect multi-site customers in every month
          they renew. Hidden when the Insights panel is collapsed. */}
      {showInsights && (
      <div className="renewal-month-strip" role="tablist" aria-label="Filter customers by renewal month"
           style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-1)', margin: 'var(--space-2) 0 var(--space-4)' }}>
        {MONTH_LABELS.map((label, i) => {
          const month = i + 1;
          const hue = MONTH_HUES[i];
          const isActive = monthFilter === month;
          const count = monthCounts[month] || 0;
          const revenue = monthRevenue[month] || 0;
          return (
            <button
              key={label}
              type="button"
              role="tab"
              aria-selected={isActive}
              onClick={() => setMonthFilter(isActive ? null : month)}
              title={`${label}: ${fmtCompactMoney(revenue)} annual value · ${count} site${count === 1 ? '' : 's'} renewing (any year)`}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '4px 10px',
                borderRadius: 'var(--radius-pill)',
                border: isActive ? '2px solid var(--color-text)' : '2px solid transparent',
                background: hue,
                color: '#fff',
                opacity: monthFilter == null || isActive ? 1 : 0.45,
                cursor: 'pointer',
                fontSize: 'var(--text-body)',
                fontWeight: isActive ? 'var(--font-semibold)' : 'var(--font-medium)',
                fontFamily: 'var(--font-mono)',
                transition: 'opacity 0.12s ease'
              }}
            >
              <span>{label}</span>
              {/* Pill shows the site count -- a multi-site customer
                  contributes to the badge per renewing site since each
                  site is its own renewal to process. The dollar total
                  lives in the tooltip + the subtitle below when a
                  month is selected. */}
              <span style={{
                background: 'rgba(255,255,255,0.28)',
                borderRadius: 'var(--radius-pill)',
                padding: '0 6px',
                fontSize: 'var(--text-caption)',
                minWidth: '16px',
                textAlign: 'center'
              }}>{count}</span>
            </button>
          );
        })}
        {monthFilter != null && (
          <button
            type="button"
            className="btn-secondary btn-sm"
            onClick={() => setMonthFilter(null)}
            style={{ marginLeft: 'var(--space-2)' }}
          >
            Clear month
          </button>
        )}
      </div>
      )}

      {/* Per-site renewal tiles for the selected month, styled like the
          Sales Leads follow-up cards. Click a tile to open the customer. */}
      {monthFilter != null && (() => {
        // Sum annual subscription value across the sites renewing this
        // month. site_annual_value is per-site (one subscription row
        // per site); the server returns 0 when no subscription exists
        // so undefined/NaN can't sneak into the total.
        const monthValue = monthTiles.reduce(
          (sum, s) => sum + Number(s.site_annual_value || 0),
          0
        );
        const fmt = (n) => `$${Math.round(n).toLocaleString('en-CA')}`;
        return (
        <div className="followup-panel">
          <div className="followup-header">
            <h3>{MONTH_LABELS[monthFilter - 1]} renewals</h3>
            <span className="followup-count">
              {/* Collapse "X of Y" to just "Y" when every site in the
                  month is actionable (X === Y); the "of Y" half is
                  redundant in that case. When X < Y the "of Y" tells
                  Dave how many are still further out. */}
              {monthTiles.length === monthAllSites.length
                ? `${monthAllSites.length} need${monthAllSites.length === 1 ? 's' : ''} attention`
                : `${monthTiles.length} of ${monthAllSites.length} need attention`}
              {' '}<span style={{ color: 'var(--color-text-dim)' }}>(due within 5 days)</span>
              {monthSummary.totalAnnual > 0 && (
                <>
                  {' · '}
                  <strong className="cell-mono">{moneyFmt(monthSummary.totalAnnual)}</strong>
                  <span style={{ color: 'var(--color-text-dim)' }}> total annual value for {MONTH_LABELS[monthFilter - 1]}</span>
                </>
              )}
            </span>
          </div>
          {monthTiles.length === 0 ? (
            <p className="empty-tab" style={{ margin: 'var(--space-3) 0' }}>
              {monthAllSites.length === 0
                ? `No site renewals in ${MONTH_LABELS[monthFilter - 1]}.`
                : `None of the ${monthAllSites.length} ${MONTH_LABELS[monthFilter - 1]} renewals are due within 5 days. Overdue + future-dated sites are visible in the customer table below.`}
            </p>
          ) : (
            <div className="followup-grid">
              {monthTiles.map((site) => (
                <RenewalSiteCard
                  key={site.site_id}
                  site={site}
                  todayISO={todayISO}
                  onClick={() => navigate(`/customers/${site.customer_id}`)}
                  onRenewed={() => { reloadSiteRenewals(); load({ keepSelection: true }); }}
                />
              ))}
            </div>
          )}
        </div>
        );
      })()}

      {/* ─── Action queue cards (Dave 2026-05-31) ───────────────────
          Three clickable cards under the search bar showing who needs
          attention. Click a card to scope the table to that bucket;
          click again to clear. Counts derived from the full customer
          list so they don't change as Dave narrows the search.
          Tucked behind the Insights toggle 2026-06-12.                */}
      {showInsights && (
      <div style={{
        display: 'flex',
        gap: 'var(--space-3)',
        margin: 'var(--space-3) 0 var(--space-3)',
        flexWrap: 'wrap'
      }}>
        {[
          { id: 'overdue',    label: 'Overdue follow-ups',    count: actionCounts.overdue,    tone: '#b91c1c' },
          { id: 'renewing30', label: 'Renewing in 30 days',   count: actionCounts.renewing30, tone: '#b45309' },
          { id: 'dark90',     label: 'Dark for 90+ days',     count: actionCounts.dark90,     tone: '#6b7280' }
        ].map((card) => {
          const active = actionFilter === card.id;
          return (
            <button
              key={card.id}
              type="button"
              onClick={() => setActionFilter(active ? null : card.id)}
              style={{
                flex: '1 1 200px',
                minWidth: 180,
                padding: 'var(--space-3) var(--space-4)',
                background: active ? card.tone : 'var(--color-surface)',
                color: active ? '#fff' : 'var(--color-text)',
                border: `1px solid ${active ? card.tone : 'var(--color-border)'}`,
                borderRadius: 'var(--radius-md)',
                cursor: 'pointer',
                textAlign: 'left',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--space-3)',
                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'background 0.15s, color 0.15s'
              }}
              title={active ? 'Click again to clear filter' : `Filter to ${card.label.toLowerCase()}`}
            >
              <span style={{
                fontSize: 'var(--text-body)',
                fontWeight: 'var(--font-semibold)'
              }}>{card.label}</span>
              <span style={{
                fontSize: 'var(--text-title, 20px)',
                fontFamily: 'var(--font-mono)',
                fontWeight: 'var(--font-bold)',
                color: active ? '#fff' : card.tone
              }}>{card.count}</span>
            </button>
          );
        })}
      </div>
      )}

      {/* ─── Colour filter chips (Dave 2026-05-31) ──────────────────
          One chip per highlight color in use. Click to scope the table
          to that color; click the active chip to clear. Counts include
          both manual highlight_color and auto Price-Increase-orange. */}
      {Object.keys(colorCounts).length > 0 && (
        <div style={{
          display: 'flex',
          gap: 'var(--space-2)',
          flexWrap: 'wrap',
          alignItems: 'center',
          marginBottom: 'var(--space-3)'
        }}>
          <span className="result-count" style={{ marginRight: 'var(--space-1)' }}>
            Filter by color:
          </span>
          {Object.entries(colorCounts).sort((a, b) => b[1] - a[1]).map(([name, count]) => {
            const active = colorFilter === name;
            const hex = HIGHLIGHT_PALETTE[name];
            return (
              <button
                key={name}
                type="button"
                onClick={() => setColorFilter(active ? null : name)}
                style={{
                  padding: '4px 10px',
                  background: active ? hex : 'var(--color-surface)',
                  color:      active ? '#fff' : hex,
                  border:     `1px solid ${hex}`,
                  borderRadius: 'var(--radius-pill, 999px)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-semibold)',
                  fontSize: 'var(--text-small)',
                  textTransform: 'capitalize'
                }}
                title={active ? 'Click again to clear' : `Show only ${LEGEND_LABEL[name] || name}-highlighted customers`}
              >
                {LEGEND_LABEL[name] || name} <span style={{ marginLeft: 4, fontFamily: 'var(--font-mono)' }}>{count}</span>
              </button>
            );
          })}
          {colorFilter && (
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={() => setColorFilter(null)}
              style={{ marginLeft: 'var(--space-1)' }}
            >
              Clear
            </button>
          )}
          {/* Price-Increase Action Required pill -- mirrors the
              dashboard panel. Visually distinct from the color pills
              (filled red instead of outlined) since it's "action
              needed" not "highlight status." Click toggles the action
              filter on the customer list to those customers only. */}
          {actionCounts.priceIncreaseAction > 0 && (() => {
            const active = actionFilter === 'priceIncreaseAction';
            return (
              <button
                type="button"
                onClick={() => setActionFilter(active ? null : 'priceIncreaseAction')}
                style={{
                  padding: '4px 10px',
                  marginLeft: 'var(--space-2)',
                  background: active ? 'var(--color-bad)' : 'var(--color-bad-bg)',
                  color:      active ? '#fff'             : 'var(--color-bad)',
                  border:     `1px solid var(--color-bad)`,
                  borderRadius: 'var(--radius-pill, 999px)',
                  cursor: 'pointer',
                  fontWeight: 'var(--font-semibold)',
                  fontSize: 'var(--text-small)'
                }}
                title={active
                  ? 'Click again to clear the price-increase filter'
                  : 'Show only customers needing a price-increase email (renewal in next 3 months, never emailed, customer ≥1 year)'}
              >
                Price Increase Action Required{' '}
                <span style={{ marginLeft: 4, fontFamily: 'var(--font-mono)' }}>
                  {actionCounts.priceIncreaseAction}
                </span>
              </button>
            );
          })()}
        </div>
      )}

      {showInsights && <ColorLegend colorCounts={colorCounts} />}

      <div className="toolbar">
        <input
          type="search"
          className="search-input"
          placeholder="Search company, site, POS, city, phone, email… (Enter to jump to a site)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={async (e) => {
            // Enter on the search box: try to resolve the query as a
            // site number. If we find a hit, jump straight to that
            // site's parent customer (Sites tab pre-selected via the
            // ?site_number= query param). Otherwise stay on the list.
            if (e.key !== 'Enter') return;
            const q = String(query).trim();
            if (!q) return;
            try {
              const r = await axios.get('/api/sites/lookup', { params: { q } });
              if (r.data && r.data.customer_id) {
                navigate(`/customers/${r.data.customer_id}?tab=sites&site_number=${encodeURIComponent(r.data.site_number || q)}`);
              }
            } catch (_) {
              // 404 / network -- no jump, leave the filter as is.
            }
          }}
          autoFocus
        />
        <span className="result-count">
          {loading ? 'Loading…' : `${filtered.length} of ${customers.length} companies · ${totalSites.toLocaleString()} sites`}
        </span>
        {/* Activity segmented control: switch between Active / All /
            Inactive-only views of the customer book. Each button shows
            the bucket count. */}
        <div style={{
          display: 'inline-flex',
          marginLeft: 'var(--space-3)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          fontSize: 'var(--text-small)'
        }}>
          {[
            { id: 'active',   label: 'Active',        count: activityCounts.active },
            { id: 'all',      label: 'All',           count: activityCounts.all },
            { id: 'inactive', label: 'Inactive only', count: activityCounts.inactiveOnly }
          ].map((opt) => {
            const active = customerStatusFilter === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                onClick={() => setCustomerStatusFilter(opt.id)}
                style={{
                  padding: '4px 10px',
                  background:  active ? 'var(--color-accent)' : 'var(--color-surface)',
                  color:       active ? '#fff' : 'var(--color-text)',
                  border:      'none',
                  borderRight: '1px solid var(--color-border)',
                  cursor: 'pointer',
                  fontWeight: active ? 'var(--font-semibold)' : 'var(--font-regular)',
                  whiteSpace: 'nowrap'
                }}
                title={`Show ${opt.label.toLowerCase()} customers`}
              >
                {opt.label} <span style={{ fontFamily: 'var(--font-mono)', marginLeft: 4 }}>{opt.count}</span>
              </button>
            );
          })}
        </div>
        {activeCount > 0 && (
          <>
            <button type="button" className="btn-secondary btn-sm" onClick={clearAll}>
              Clear Filters ({activeCount})
            </button>
            <span className="result-count" style={{ marginLeft: 'var(--space-2)' }}>
              {filteredSites.toLocaleString()} location{filteredSites === 1 ? '' : 's'}
            </span>
          </>
        )}
        {/* Merge to Parent — appears once ≥1 customer is selected. The
            button itself is only ENABLED at ≥2 (or 1 + create-new-parent
            mode inside the dialog). Selection survives column-filter
            changes since the IDs aren't tied to row visibility. */}
        {selectedIds.size > 0 && (
          <div className="toolbar-selection">
            <span className="result-count">{selectedIds.size} selected</span>
            <button
              type="button"
              className="btn-primary btn-sm"
              onClick={() => setMergeOpen(true)}
              title="Convert the selected customers into Sites under one parent"
            >
              Merge to Parent…
            </button>
            <button
              type="button"
              className="btn-secondary btn-sm"
              onClick={clearSelection}
            >
              Clear
            </button>
          </div>
        )}
        {/* Bulk-refresh xsiteio Status. One click hits all_site_informations
            on xsiteio + UPDATEs every linked local site's xsiteio_active +
            last_login + last_updated. Takes seconds for ~300 sites. */}
        <button
          type="button"
          className="btn-secondary btn-sm"
          onClick={async () => {
            if (bulkPullBusy) return;
            if (!window.confirm(
              'Refresh xsiteio Status for every linked site?\n\n' +
              'This fetches all_site_informations from xsiteio once and ' +
              'updates the local xsiteio_active flag (plus last login / last updated) ' +
              'for every site Command has linked. No xsiteio writes — read-only refresh. ' +
              'Takes 10–60 seconds.'
            )) return;
            setBulkPullBusy(true);
            try {
              const r = await axios.post('/api/sites/xsiteio/bulk-pull');
              const d = r.data;
              const lines = [
                `Bulk Pull complete.`,
                ``,
                `Linked sites: ${d.total}`,
                `Matched: ${d.matched} (no match: ${d.no_match})`,
                ``,
                `Status (in xsiteio right now):`,
                `  ACTIVE: ${d.active_in_xsiteio ?? d.flipped_active ?? 0}`,
                `  INACTIVE: ${d.inactive_in_xsiteio ?? d.flipped_inactive ?? 0}`,
                `  UNKNOWN: ${d.unknown_status ?? 0}`,
                ``,
                `Data populated locally:`,
                `  SFTP credentials: ${d.sftp_credentials_set ?? 0}`,
                `  Subscription rows: ${d.subscription_rows_set ?? 0}`,
                ``,
                `Failures: ${d.failures.length}`
              ];
              alert(lines.join('\n'));
              window.location.reload();
            } catch (err) {
              alert(`Bulk Pull failed: ${err.response?.data?.error || err.message}`);
            } finally {
              setBulkPullBusy(false);
            }
          }}
          disabled={bulkPullBusy}
          title="Fetch xsiteio's site list once and refresh every linked site's status"
        >
          {bulkPullBusy ? 'Refreshing…' : '↻ Refresh xsiteio Status'}
        </button>
        <div className="toolbar-prefs">
          <LayoutDropdown
            layouts={layouts}
            activeName={activeName}
            onChange={setActiveName}
          />
          <ColumnToggle
            columns={COLUMNS.map(({ key, label, group }) => ({ key, label, group }))}
            value={visibleKeys}
            onChange={setVisibleKeys}
            activeName={activeName}
            onSaveAs={(name) => saveLayout(name, visibleKeys)}
            onDelete={deleteLayout}
          />
        </div>
      </div>

      {error && <p className="error">Error: {error}</p>}

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              <th className="th-checkbox">
                {/* Master select — toggles every currently-filtered row.
                    Indeterminate when some, but not all, filtered rows
                    are selected. */}
                <input
                  type="checkbox"
                  aria-label="Select all visible customers"
                  checked={
                    filtered.length > 0 &&
                    filtered.every((c) => selectedIds.has(c.id))
                  }
                  ref={(node) => {
                    if (!node) return;
                    const some = filtered.some((c) => selectedIds.has(c.id));
                    const all  = filtered.length > 0 && filtered.every((c) => selectedIds.has(c.id));
                    node.indeterminate = some && !all;
                  }}
                  onChange={(e) => {
                    setSelectedIds((prev) => {
                      const next = new Set(prev);
                      if (e.target.checked) {
                        for (const c of filtered) next.add(c.id);
                      } else {
                        for (const c of filtered) next.delete(c.id);
                      }
                      return next;
                    });
                  }}
                />
              </th>
              {visibleColumns.map((c) => {
                const filterable = typeof c.filterValue === 'function';
                const isActive = filterable && !!filters[c.key];
                // Sort toggle: click the header label area to sort by
                // this column. Same column flips asc <-> desc; new
                // column starts asc. Click the filter funnel separately
                // to open the value-picker (existing behavior unchanged).
                const isSortCol = sortBy === c.key;
                const arrow = isSortCol ? (sortDir === 'desc' ? ' ▾' : ' ▴') : '';
                function handleHeaderClick() {
                  if (isSortCol) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
                  else { setSortBy(c.key); setSortDir('asc'); }
                }
                return (
                  <th key={c.key} className={isActive ? 'th-filter-active' : ''}>
                    <span
                      className="th-label"
                      role="button"
                      tabIndex={0}
                      onClick={handleHeaderClick}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleHeaderClick(); } }}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                      title={isSortCol ? `Sorted ${sortDir} - click to reverse` : `Click to sort by ${c.label}`}
                    >
                      {c.label}{arrow}
                    </span>
                    {filterable && (
                      <ColumnFilterMenu
                        label={c.label}
                        values={valuesByKey[c.key] || []}
                        selected={filters[c.key]}
                        onChange={(set) => setFilter(c.key, set)}
                        onClear={() => clearFilter(c.key)}
                      />
                    )}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {!loading && filtered.length === 0 && (
              <tr>
                <td colSpan={visibleColumns.length + 1} className="empty-row">
                  {customers.length === 0 ? 'No customers yet.' : 'No customers match your search.'}
                </td>
              </tr>
            )}
            {filtered.map((c) => {
              const isSelected = selectedIds.has(c.id);
              // Customers with all-inactive sites get a subtle italic +
              // dimmer text so they're visually distinct without being
              // unreadable. They only appear when the include-inactive
              // toggle is on.
              const isInactiveOnly = (Number(c.site_count) || 0) > 0
                && (Number(c.active_site_count) || 0) === 0;
              return (
                <tr
                  key={c.id}
                  onClick={() => navigate(`/customers/${c.id}`)}
                  className={`row-clickable${isSelected ? ' row-selected' : ''}`}
                  style={isInactiveOnly ? {
                    fontStyle: 'italic',
                    color: 'var(--color-text-dim)'
                  } : undefined}
                  title={isInactiveOnly ? 'All sites for this customer are Inactive' : undefined}
                >
                  <td
                    className="td-checkbox"
                    onClick={(e) => { e.stopPropagation(); toggleSelected(c.id); }}
                  >
                    <input
                      type="checkbox"
                      aria-label={`Select ${c.company || c.last_name || c.id}`}
                      checked={isSelected}
                      onChange={() => toggleSelected(c.id)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </td>
                  {visibleColumns.map((col) => (
                    <td key={col.key} className={col.tdClass || ''}>
                      {col.cell ? col.cell(c, { todayISO }) : ''}
                    </td>
                  ))}
                </tr>
              );
            })}
                    </tbody>
        </table>
      </div>

      {/* Merge-to-Parent dialog (mounted here so it overlays the page).
          MergeToParentDialog expects full customer objects, not just IDs,
          so we hydrate from the customers state on every render. Cheap
          because selectedIds is normally tiny. Closing the dialog clears
          mergeOpen; onMerged also refreshes the list + clears selection. */}
      <MergeToParentDialog
        open={mergeOpen}
        selectedCustomers={customers.filter((c) => selectedIds.has(c.id))}
        onClose={() => setMergeOpen(false)}
        onMerged={() => {
          setMergeOpen(false);
          setSelectedIds(new Set());
          load();
        }}
      />
    </section>
  );
}
